import { useInput } from "@redotech/react-util/form";
import { useTriggerLoad } from "@redotech/react-util/load";
import { input } from "@redotech/ui/form";
import { emailValidator } from "@redotech/web-util/form";
import { memo, useEffect, useState } from "react";
import { Button, ButtonBorder, ButtonSize, ButtonTheme } from "../button";
import { Checkbox } from "../checkbox";
import { Flex } from "../flex";
import { InputSize } from "../input";
import { ExternalLink } from "../link";
import { OtpInput } from "../otp-input";
import { Header, Text } from "../text";
import { FormTextInput } from "../text-input";
import { TextSizeValue, TextWeightValue } from "../theme/typography";
import {
  getLocalStorageWithExpiry,
  setLocalStorageWithExpiry,
} from "../utils/local-storage-wrapper";
import * as redoLoginCss from "./redo-login.module.css";

const OTP_INFO_KEY = "redo.otp_info";
const RESEND_CODE_DELAY = 30;

interface OtpInfo {
  email: string;
  fourDigitCodeId: string;
  lastSentAt: number;
}

export interface SubmitOneTimePasscodeEmailInputParams {
  email: string;
}

interface SubmitOneTimePasscodeEmailOutput {
  fourDigitCodeId: string;
}

export interface VerifyOneTimePasscodeInputParams {
  fourDigitCodeId: string;
  passcode: string;
  email: string;
  teamId: string;
  currentLocation?: string;
}

interface VerifyOneTimePasscodeOutput {
  success: boolean;
  customerAuthToken: string | undefined;
  multipassUrl: string | undefined;
}

interface RedoLoginProps {
  onLogin?: (authToken: string) => Promise<void>;
  otpParams: {
    submitOneTimePasscodeEmail: (
      params: SubmitOneTimePasscodeEmailInputParams,
    ) => Promise<SubmitOneTimePasscodeEmailOutput>;
    verifyOneTimePasscode: (
      params: VerifyOneTimePasscodeInputParams,
    ) => Promise<VerifyOneTimePasscodeOutput>;
  };
  promptParams?:
    | {
        promptText: string;
        promptSubtext?: string;
      }
    | undefined;
  setAuth: ((authToken: string | undefined) => void) | undefined;
  submitButtonParams?:
    | {
        size?: ButtonSize | undefined;
        sendOneTimePasscodeButtonText?: string | undefined;
      }
    | undefined;
  teamId: string;
  textParams?: {
    fontSize?: TextSizeValue | undefined;
    fontWeight?: TextWeightValue | undefined;
  };
  showMarketingCheckbox: boolean;
  subscribeToMarketing: (email: string, agreementText: string) => void;
}

export const RedoLogin = memo(function RedoLogin({
  onLogin,
  otpParams,
  promptParams,
  setAuth,
  submitButtonParams,
  teamId,
  textParams,
  showMarketingCheckbox,
  subscribeToMarketing,
}: RedoLoginProps) {
  const otpInfo: OtpInfo | null = getLocalStorageWithExpiry(OTP_INFO_KEY, {
    returnNullIfMissingExpiry: true,
  });
  const [emailEntered, setEmailEntered] = useState<string>(
    otpInfo?.email || "",
  );
  const [fourDigitCodeId, setFourDigitCodeId] = useState<string>(
    otpInfo?.fourDigitCodeId || "",
  );
  const [secondsSinceLastSent, setSecondsSinceLastSent] = useState<
    number | undefined
  >(
    otpInfo
      ? Temporal.Now.instant().epochSeconds - otpInfo.lastSentAt
      : undefined,
  );
  const [showIncorrectCodeError, setShowIncorrectCodeError] = useState(false);

  const emailInput = useInput(input<string>({ validator: emailValidator }), "");
  const fourDigitCodeInput = useInput(input<Array<string>>(), ["", "", "", ""]);

  const [marketingAccepted, setMarketingAccepted] = useState<boolean>(true);

  const [emailSubmit, doEmailSubmit] = useTriggerLoad(async () => {
    const email = (emailInput.value || emailEntered).toLowerCase();
    const response = await otpParams.submitOneTimePasscodeEmail({
      email,
    });
    setEmailEntered(email);
    setFourDigitCodeId(response.fourDigitCodeId);

    setLocalStorageWithExpiry(
      OTP_INFO_KEY,
      {
        email,
        fourDigitCodeId: response.fourDigitCodeId,
        lastSentAt: Temporal.Now.instant().epochSeconds,
      },
      0.25,
    );
  });

  const [fourDigitCodeSubmit, doFourDigitCodeSubmit] = useTriggerLoad(
    async () => {
      const response = await otpParams.verifyOneTimePasscode({
        fourDigitCodeId,
        passcode: fourDigitCodeInput.value.join(""),
        email: emailEntered,
        teamId,
        currentLocation: window.location.href,
      });

      const token = response.customerAuthToken;

      if (response.success && token) {
        setAuth && setAuth(token);
        localStorage.removeItem(OTP_INFO_KEY);

        if (response.multipassUrl) {
          await fetch(response.multipassUrl, {
            credentials: "include",
            redirect: "manual",
          });
        }

        await onLogin?.(token);

        if (showMarketingCheckbox && marketingAccepted) {
          subscribeToMarketing(emailEntered, subscribeToMarketingText);
        }
      } else {
        const submitResponse = await otpParams.submitOneTimePasscodeEmail({
          email: emailEntered,
        });
        setFourDigitCodeId(submitResponse.fourDigitCodeId);

        setLocalStorageWithExpiry(
          OTP_INFO_KEY,
          {
            email: emailEntered,
            fourDigitCodeId: submitResponse.fourDigitCodeId,
            lastSentAt: Temporal.Now.instant().epochSeconds,
          },
          0.25,
        );

        fourDigitCodeInput.setValue(["", "", "", ""]);
        setShowIncorrectCodeError(true);
      }
    },
  );

  useEffect(() => {
    const otpInfo: OtpInfo | null = getLocalStorageWithExpiry(OTP_INFO_KEY, {
      returnNullIfMissingExpiry: true,
    });
    if (otpInfo) {
      setSecondsSinceLastSent(
        Temporal.Now.instant().epochSeconds - otpInfo.lastSentAt,
      );
    }

    const interval = setInterval(() => {
      const otpInfo: OtpInfo | null = getLocalStorageWithExpiry(OTP_INFO_KEY, {
        returnNullIfMissingExpiry: true,
      });
      if (!otpInfo) {
        setSecondsSinceLastSent(undefined);
        return;
      }

      setSecondsSinceLastSent(
        Temporal.Now.instant().epochSeconds - otpInfo.lastSentAt,
      );
    }, 1000);
    return () => clearInterval(interval);
  }, [fourDigitCodeId]);

  const handleEmailSubmit = async (e: any) => {
    e.preventDefault();
    doEmailSubmit();
  };

  const handleFourDigitCodeSubmit = async (e: any) => {
    e.preventDefault();
    doFourDigitCodeSubmit();
  };

  const handleChangeEmail = () => {
    localStorage.removeItem(OTP_INFO_KEY);
    setEmailEntered("");
    setFourDigitCodeId("");
    setShowIncorrectCodeError(false);
    emailInput.setValue("");
  };

  const subscribeToMarketingText = "Email me with news and offers";
  return (
    <>
      {!emailEntered && promptParams && (
        <>
          <Header fontFamily="inherit" fontSize="xs">
            {promptParams.promptText}
          </Header>
          {promptParams.promptSubtext && (
            <Text fontSize="sm">{promptParams.promptSubtext}</Text>
          )}
        </>
      )}
      <Flex dir="column" gap="xl">
        {!!emailEntered && fourDigitCodeId ? (
          <>
            <Text
              fontSize={textParams?.fontSize}
              fontWeight={textParams?.fontWeight}
            >
              We sent a 4-digit code to <strong>{emailEntered}</strong>. Please
              enter the code below.
            </Text>
            <Flex
              as="form"
              dir="column"
              gap="xl"
              onSubmit={handleFourDigitCodeSubmit}
            >
              <OtpInput
                numeric
                onChange={(value) => {
                  fourDigitCodeInput.setValue(value);
                  if (value.join("").length === 4) {
                    doFourDigitCodeSubmit();
                  }
                }}
                values={fourDigitCodeInput.value}
              />
              <Button
                border={ButtonBorder.TRANSPARENT}
                disabled={
                  fourDigitCodeInput.errors.length > 0 ||
                  !fourDigitCodeInput.value ||
                  fourDigitCodeSubmit.pending
                }
                pending={fourDigitCodeSubmit.pending}
                size={submitButtonParams?.size ?? ButtonSize.SMALL}
                theme={ButtonTheme.PRIMARY}
                type="submit"
              >
                <Text
                  fontSize={textParams?.fontSize}
                  fontWeight={textParams?.fontWeight}
                >
                  Submit
                </Text>
              </Button>
              {(secondsSinceLastSent === undefined ||
                secondsSinceLastSent > RESEND_CODE_DELAY) &&
              !emailSubmit.pending ? (
                <ExternalLink
                  className={redoLoginCss.secondaryOption}
                  onClick={handleEmailSubmit}
                  showIcon={false}
                >
                  <Text
                    fontSize={textParams?.fontSize}
                    fontWeight={textParams?.fontWeight}
                  >
                    Send another code
                  </Text>
                </ExternalLink>
              ) : (
                <Text
                  className={redoLoginCss.secondaryOption}
                  fontSize={textParams?.fontSize}
                  fontWeight={textParams?.fontWeight}
                  textColor="disabled"
                >
                  Send another code
                  {!emailSubmit.pending &&
                    secondsSinceLastSent !== undefined && (
                      <> ({RESEND_CODE_DELAY - secondsSinceLastSent + 1})</>
                    )}
                </Text>
              )}
              <ExternalLink
                className={redoLoginCss.secondaryOption}
                onClick={handleChangeEmail}
                showIcon={false}
              >
                <Text
                  fontSize={textParams?.fontSize}
                  fontWeight={textParams?.fontWeight}
                >
                  Change email
                </Text>
              </ExternalLink>
            </Flex>
            {showIncorrectCodeError && (
              <Text
                fontSize={textParams?.fontSize}
                fontWeight={textParams?.fontWeight}
                textColor="error"
              >
                The code you entered is incorrect. Please check your email for
                another code and try again.
              </Text>
            )}
          </>
        ) : (
          <Flex as="form" dir="column" gap="xl" onSubmit={handleEmailSubmit}>
            <FormTextInput
              input={emailInput}
              label="Email"
              placeholder="Enter your email"
              size={InputSize.EXTRA_SMALL}
              type="email"
            />
            {showMarketingCheckbox && (
              <Checkbox
                onChange={(checked) => setMarketingAccepted(checked)}
                value={marketingAccepted}
              >
                {subscribeToMarketingText}
              </Checkbox>
            )}
            <Button
              border={ButtonBorder.TRANSPARENT}
              disabled={
                emailInput.errors.length > 0 ||
                !emailInput.value ||
                emailSubmit.pending
              }
              pending={emailSubmit.pending}
              size={submitButtonParams?.size ?? ButtonSize.SMALL}
              theme={ButtonTheme.PRIMARY}
              type="submit"
            >
              <Text
                fontSize={textParams?.fontSize}
                fontWeight={textParams?.fontWeight}
              >
                {submitButtonParams?.sendOneTimePasscodeButtonText ??
                  "Send one-time passcode"}
              </Text>
            </Button>
          </Flex>
        )}
      </Flex>
    </>
  );
});
